import { useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { DOSHA_QUIZ, computeDosha } from "@/lib/doshaQuiz";
import { http } from "@/lib/api";
import { toast } from "sonner";
import ProductCard from "@/components/ProductCard";
import { Sparkles, ArrowRight, ArrowLeft, CheckCircle2, RefreshCcw, Leaf, Flame, Mountain, Circle } from "lucide-react";

const DOSHA_ICON = { vata: Leaf, pitta: Flame, kapha: Mountain, tridoshic: Circle };

export default function DoshaQuiz() {
  const nav = useNavigate();
  const [step, setStep] = useState("intro"); // intro | q | lead | result
  const [qIdx, setQIdx] = useState(0);
  const [answers, setAnswers] = useState([]); // dosha strings per question
  const [lead, setLead] = useState({ name: "", email: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);
  const [products, setProducts] = useState([]);

  const q = DOSHA_QUIZ.questions[qIdx];
  const totalQ = DOSHA_QUIZ.questions.length;
  const progress = step === "q" ? ((qIdx) / totalQ) * 100 : step === "lead" ? 90 : step === "result" ? 100 : 0;

  const result = useMemo(() => (answers.length === totalQ ? computeDosha(answers) : null), [answers, totalQ]);
  const resultData = result ? DOSHA_QUIZ.results[result.dominant] : null;
  const Icon = result ? DOSHA_ICON[result.dominant] : Sparkles;

  const startQuiz = () => { setStep("q"); setQIdx(0); setAnswers([]); };

  const answer = (dosha) => {
    const next = [...answers.slice(0, qIdx), dosha];
    setAnswers(next);
    if (qIdx + 1 < totalQ) {
      setTimeout(() => setQIdx(qIdx + 1), 200);
    } else {
      setTimeout(() => setStep("lead"), 250);
    }
  };

  const back = () => {
    if (qIdx === 0) setStep("intro");
    else setQIdx(qIdx - 1);
  };

  const submitLead = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const finalResult = computeDosha(answers);
      const { data } = await http.post("/quiz/submit", {
        quiz_id: DOSHA_QUIZ.quiz_id,
        answers,
        dominant_dosha: finalResult.dominant,
        counts: finalResult.counts,
        lead,
      });
      setProducts(data.recommended_products || []);
      setStep("result");
    } catch (submitErr) {
      console.error("Quiz submit failed, falling back to local products", submitErr);
      // Still show the result even if backend save fails — client-side compute is source of truth
      try {
        const { data } = await http.get("/products", { params: { dosha: computeDosha(answers).dominant } });
        setProducts(data.slice(0, 6));
      } catch (fallbackErr) {
        console.error("Fallback product fetch failed", fallbackErr);
      }
      setStep("result");
      toast.warning("Result generated locally");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-[80vh] bg-gradient-to-b from-[#FAF8F5] to-[#E8F0EA]/40" data-testid="dosha-quiz-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {step !== "intro" && step !== "result" && (
          <div className="mb-6">
            <div className="flex items-center justify-between text-xs font-mono uppercase text-[#5A6B5D] mb-2">
              <span>Step {step === "q" ? qIdx + 1 : totalQ + 1} of {totalQ + 1}</span>
              <span>{Math.round(progress)}% complete</span>
            </div>
            <div className="h-1.5 bg-[#E2DDD5] rounded-full overflow-hidden">
              <div className="h-full bg-[#1E3A2B] transition-all duration-300" style={{ width: `${progress}%` }} />
            </div>
          </div>
        )}

        {step === "intro" && (
          <div className="text-center py-10 fade-slide-up" data-testid="quiz-intro">
            <span className="badge-sage inline-flex items-center gap-1"><Sparkles className="w-3 h-3" /> Curated by Dr. Rupali Srivastava</span>
            <h1 className="mt-5 font-serif text-4xl sm:text-5xl font-bold text-[#1A201C] tracking-tight">Discover Your Ayurvedic Prakriti</h1>
            <p className="mt-4 text-[#5A6B5D] max-w-xl mx-auto">
              A 10-question guided assessment rooted in classical Ayurveda. In ~3 minutes, you'll know your dominant Dosha — Vata, Pitta, or Kapha — and receive product recommendations personalised to your constitution.
            </p>
            <div className="mt-8 grid sm:grid-cols-3 gap-3 max-w-lg mx-auto text-left">
              {["Physical traits", "Digestion & sleep", "Mind & behaviour"].map((c) => (
                <div key={c} className="card-warm p-4">
                  <CheckCircle2 className="w-4 h-4 text-[#D97706]" />
                  <div className="mt-2 text-sm font-serif font-semibold text-[#1A201C]">{c}</div>
                </div>
              ))}
            </div>
            <button onClick={startQuiz} className="mt-10 btn-forest inline-flex items-center gap-2" data-testid="quiz-start-button">
              Begin Assessment <ArrowRight className="w-4 h-4" />
            </button>
            <p className="mt-4 text-[11px] font-mono text-[#5A6B5D]">Free · No account needed · Educational purposes only</p>
          </div>
        )}

        {step === "q" && (
          <div className="fade-slide-up" data-testid={`quiz-question-${q.id}`}>
            <div className="text-xs font-mono uppercase text-[#D97706] tracking-wider">{q.category} · Question {q.id}</div>
            <h2 className="mt-3 font-serif text-3xl font-bold text-[#1A201C] leading-tight">{q.question}</h2>
            <div className="mt-6 space-y-3">
              {q.options.map((opt, i) => {
                const selected = answers[qIdx] === opt.dosha;
                return (
                  <button
                    key={i}
                    onClick={() => answer(opt.dosha)}
                    data-testid={`quiz-q${q.id}-option-${opt.dosha}`}
                    className={`w-full text-left p-5 rounded-2xl border-2 transition-all ${selected ? "border-[#1E3A2B] bg-[#E8F0EA]" : "border-[#E2DDD5] bg-white hover:border-[#1E3A2B] hover:bg-[#FAF8F5]"}`}
                  >
                    <div className="flex items-start gap-3">
                      <span className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${selected ? "bg-[#1E3A2B] text-white" : "bg-[#FAF8F5] border border-[#E2DDD5] text-[#5A6B5D]"}`}>
                        {String.fromCharCode(65 + i)}
                      </span>
                      <span className="text-[15px] text-[#1A201C] leading-relaxed pt-1">{opt.text}</span>
                    </div>
                  </button>
                );
              })}
            </div>
            <div className="mt-6 flex justify-between">
              <button onClick={back} className="text-sm text-[#5A6B5D] hover:text-[#1E3A2B] inline-flex items-center gap-1" data-testid="quiz-back-button">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              <span className="text-xs font-mono text-[#5A6B5D]">Tap an option to continue</span>
            </div>
          </div>
        )}

        {step === "lead" && (
          <form onSubmit={submitLead} className="fade-slide-up bg-white border border-[#E2DDD5] rounded-3xl p-6 sm:p-10 shadow-lg" data-testid="quiz-lead-form">
            <span className="badge-sage inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Assessment complete</span>
            <h2 className="mt-4 font-serif text-3xl font-bold text-[#1A201C]">Your Dosha profile is ready</h2>
            <p className="mt-2 text-[#5A6B5D] text-sm">
              Where should we send your personalised Dosha report and product recommendations? Amiroop Healthion LLP will keep this private — no spam.
            </p>
            <div className="mt-6 space-y-3">
              <Field label="Your Name" value={lead.name} onChange={(v) => setLead({ ...lead, name: v })} testid="lead-name" required />
              <Field label="Email" type="email" value={lead.email} onChange={(v) => setLead({ ...lead, email: v })} testid="lead-email" required />
              <Field label="Phone (optional, for WhatsApp updates)" value={lead.phone} onChange={(v) => setLead({ ...lead, phone: v })} testid="lead-phone" />
            </div>
            <button disabled={submitting} className="mt-6 w-full btn-forest inline-flex items-center justify-center gap-2" data-testid="lead-submit-button">
              {submitting ? "Analysing your profile…" : "Reveal My Dosha Result →"}
            </button>
            <p className="mt-3 text-[10px] font-mono text-[#5A6B5D] text-center">By continuing you agree to receive occasional wellness content from Healthion India. Unsubscribe anytime.</p>
          </form>
        )}

        {step === "result" && resultData && (
          <div className="fade-slide-up" data-testid={`quiz-result-${result.dominant}`}>
            <div className="rounded-3xl overflow-hidden border border-[#E2DDD5]">
              <div className="p-8 sm:p-10 text-white" style={{ background: `linear-gradient(135deg, #1E3A2B 0%, ${resultData.accent}30 100%), #1E3A2B` }}>
                <Icon className="w-10 h-10" style={{ color: resultData.accent }} />
                <div className="mt-4 text-xs font-mono uppercase tracking-wider opacity-70">{resultData.elements}</div>
                <h1 className="mt-2 font-serif text-4xl sm:text-5xl font-bold tracking-tight">{resultData.title}</h1>
                <div className="mt-2 text-lg italic opacity-90">{resultData.tagline}</div>
                <p className="mt-6 opacity-85 leading-relaxed max-w-2xl">{resultData.description}</p>
                <div className="mt-6 flex flex-wrap gap-3 text-xs">
                  {Object.entries(result.counts).map(([k, v]) => (
                    <div key={k} className="bg-white/10 border border-white/20 rounded-full px-3 py-1.5 font-mono uppercase">
                      {k}: {v}/10
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-white p-6 sm:p-8">
                <div className="text-xs font-mono uppercase text-[#D97706]">Recommended Ayurvedic Herbs</div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {resultData.recommended_herbs.map((h) => (
                    <span key={h} className="badge-sage" style={{ background: "#E8F0EA" }}>{h}</span>
                  ))}
                </div>
                <p className="mt-4 text-xs text-[#5A6B5D] italic">
                  Note · This is a Prakriti self-assessment for educational purposes only and is not medical advice.
                  Consult a Registered Medical Practitioner for personalised diagnosis.
                </p>
              </div>
            </div>

            <div className="mt-10">
              <div className="flex items-end justify-between mb-5">
                <div>
                  <div className="text-xs font-mono text-[#D97706] uppercase tracking-wider">Curated for your Prakriti</div>
                  <h3 className="font-serif text-2xl font-bold text-[#1A201C] mt-1">Products from verified vendors</h3>
                </div>
                <Link to={`/products?dosha=${result.dominant === "tridoshic" ? "tridoshic" : result.dominant}`} className="text-sm text-[#1E3A2B] font-semibold hover:text-[#D97706]">See all →</Link>
              </div>
              {products.length === 0 ? (
                <p className="text-sm text-[#5A6B5D] py-6 text-center bg-white border border-[#E2DDD5] rounded-2xl">No matching products just yet. Browse the full catalog.</p>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {products.slice(0, 6).map((p) => <ProductCard key={p.id} product={p} />)}
                </div>
              )}
            </div>

            <div className="mt-8 flex flex-wrap gap-3 justify-center">
              <button onClick={() => { setStep("intro"); setAnswers([]); setQIdx(0); setProducts([]); }} className="btn-outline-forest inline-flex items-center gap-2" data-testid="quiz-retake-button">
                <RefreshCcw className="w-4 h-4" /> Retake Quiz
              </button>
              <button onClick={() => nav("/products")} className="btn-forest inline-flex items-center gap-2">
                Shop Marketplace <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, type = "text", value, onChange, testid, required }) {
  return (
    <div>
      <label className="text-xs font-mono uppercase text-[#5A6B5D]">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        data-testid={testid}
        className="mt-1 w-full border border-[#E2DDD5] rounded-lg p-3 text-sm bg-white outline-[#1E3A2B]"
      />
    </div>
  );
}
